/**
 * «¿QUÉ COBRO SE DEVUELVE?» (revisión del 15/10/2026)
 * --------------------------------------------------
 * Una devolución entera del pago que dejó pagada la reserva de una cita no
 * se notaba si al paciente le quedaba otro dinero suelto (una consulta
 * vieja): la cita seguía pagada. Ahora, al apuntar una devolución de un
 * paciente con citas con la reserva pagada por cobros SIN presupuesto, se
 * pregunta cuál se devuelve (backend/services/reserva_cita.py,
 * `devuelve_cobro_id`):
 *   · uno de esos cobros: devuelto entero, su cita vuelve a «sin pagar»;
 *   · «Otro / ninguno de estos» (`DEVUELVE_NINGUNO`): no toca ninguna cita.
 * Es obligatorio cuando sale y nada viene elegido de antemano. Lo usan
 * Cobros, la ficha (cobro rápido) y Facturas y devoluciones.
 */
import React, { useEffect, useState } from 'react';
import { api, formatDate, formatEUR } from '../../lib/api';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

/** «Otro / ninguno de estos» (services/reserva_cita.DEVUELVE_NINGUNO). */
export const DEVUELVE_NINGUNO = 'ninguno';

/** «02/01/2099 · Visita · 80,00 € · paga la cita del 15/01/2099…». */
export function textoCobroDevuelto(k) {
  return [formatDate(k.date), k.concept, formatEUR(k.total), k.cita ? `paga la ${k.cita}` : 'reserva sin cita']
    .filter(Boolean).join(' · ');
}

/**
 * Estado del campo: `activo` (es una devolución), la lista de cobros que se
 * pueden devolver de ese paciente, lo elegido y lo que va en la devolución
 * (`devuelveCobroId`: el id, «ninguno» o '' si no se pregunta).
 */
export function useCobroQueSeDevuelve(patientId, activo) {
  const [cargados, setCargados] = useState({ pid: '', lista: [] });
  const [elegido, setElegido] = useState('');
  useEffect(() => {
    setCargados({ pid: '', lista: [] });
    setElegido('');
    if (!patientId || !activo) return undefined;
    let vigente = true;
    api.get('/api/cobros/reservas-cobradas', { params: { patient_id: patientId } })
      .then((r) => {
        if (vigente) setCargados({ pid: patientId, lista: Array.isArray(r.data) ? r.data : [] });
      })
      // Sin la lista, el servidor decide con lo que sabe (y lo dice en el rastro).
      .catch(() => { if (vigente) setCargados({ pid: '', lista: [] }); });
    return () => { vigente = false; };
  }, [patientId, activo]);
  const lista = activo && cargados.pid === patientId ? cargados.lista : [];
  const pregunta = lista.length > 0;
  const valido = pregunta && (elegido === DEVUELVE_NINGUNO || lista.some((k) => k.id === elegido));
  return {
    pregunta,
    lista,
    elegido: valido ? elegido : '',
    setElegido,
    falta: pregunta && !valido,
    devuelveCobroId: valido ? elegido : '',
  };
}

/** El campo. `prefijo` da los data-testid: `<prefijo>` y `<prefijo>-select`. */
export function QueCobroSeDevuelveField({ estado, prefijo = 'cobro-devuelve', className = '' }) {
  if (!estado.pregunta) return null;
  return (
    <div className={className} data-testid={prefijo}>
      <Label>¿Qué cobro se devuelve? <span className="text-red-600">*</span></Label>
      <Select value={estado.elegido} onValueChange={estado.setElegido}>
        <SelectTrigger className="mt-1.5" data-testid={`${prefijo}-select`}>
          <SelectValue placeholder="— Contesta: ¿qué cobro se devuelve? —" />
        </SelectTrigger>
        <SelectContent>
          {estado.lista.map((k) => (
            <SelectItem key={k.id} value={k.id}>{textoCobroDevuelto(k)}</SelectItem>
          ))}
          <SelectItem value={DEVUELVE_NINGUNO}>Otro / ninguno de estos</SelectItem>
        </SelectContent>
      </Select>
      <div className="text-xs text-muted-foreground mt-1">
        Este paciente tiene la reserva de alguna cita pagada con estos cobros. Si devuelves uno entero,
        su cita vuelve a quedar sin pagar.
      </div>
    </div>
  );
}
